import { SidebarNavListItem } from 'enums/sidebar'

interface FindSidebarPanelsProps {
  sidebarPanelName: string
  sidebarLinks: SidebarNavListItem[]
  /**
   * e.g. '/outlets/active-company-wales/customers'
   */
  currentPath: string
  /**
   * The only part of the path we care about is what's after the outletSlug
   * e.g. '/customers'
   */
  relevantCurrentPath: string
}

interface DetermineSidebarBreadcrumbProps
  extends Pick<FindSidebarPanelsProps, 'currentPath' | 'relevantCurrentPath'> {
  sidebarMenus: (
    | {
        name: string
        collection?: SidebarNavListItem[]
      }
    | undefined
  )[]
}

type SidebarBreadcrumb = string[] | undefined

/**
 * Given our current path, find the breadcrumbs to the closest sidebar link
 * e.g. Current path = 'outlets/active-company-wales/widgets/acw/edit'
 * Closest sidebar link = My Widgets [outlets/active-company-wales/widgets]
 * Return breadcrumb: [ "Sidebar", "Toolset", "Widget" ]
 */
const determineSidebarBreadcrumbForCurrentPath = ({
  sidebarMenus,
  currentPath,
  relevantCurrentPath
}: DetermineSidebarBreadcrumbProps): SidebarBreadcrumb => {
  if (relevantCurrentPath === '') return

  let sidebarBreadcrumb: SidebarBreadcrumb = undefined

  sidebarMenus.forEach(menu => {
    if (!menu || !menu?.collection) return

    const validPanels = findSidebarPanels({
      sidebarPanelName: menu.name,
      sidebarLinks: menu.collection,
      currentPath,
      relevantCurrentPath
    })

    if (validPanels?.length) {
      sidebarBreadcrumb = validPanels
    }
  })

  return sidebarBreadcrumb
}

export default determineSidebarBreadcrumbForCurrentPath

/**
 * FindSidebarPanels is used recursively.
 * Once we find a sidebar panel that the current path is contained within,
 * we enter that panel and search the panels within for the next panel that
 * the current path is contained within.
 * This builds the array of panels that lead to the closest sidebar link to the
 * current path.
 */
const findSidebarPanels = ({
  sidebarPanelName,
  sidebarLinks,
  currentPath,
  relevantCurrentPath
}: FindSidebarPanelsProps): SidebarBreadcrumb => {
  const sidebarBreadcrumb: string[] = []

  if (!sidebarLinks) return

  sidebarLinks.forEach(sidebarLink => {
    if (
      sidebarLink.url &&
      currentPathBelongsToSidebarPath({
        currentPath: currentPath,
        sidebarLinkUrl: sidebarLink.url,
        relevantCurrentPath
      })
    ) {
      sidebarBreadcrumb.push(sidebarPanelName)
      return
    }

    let subnavigationPaths =
      sidebarLink?.collection &&
      findSidebarPanels({
        sidebarPanelName: sidebarLink.name,
        sidebarLinks: sidebarLink.collection,
        currentPath,
        relevantCurrentPath
      })

    // Remove duplication e.g. ['Widget', 'Widget']
    // This is because if there are two sidebar links within a panel that can match against the current path,
    // this panel will get added to the array twice.
    // For example, the Widget panel contains the links 'My Widgets' and 'Widget Settings'.
    // If you are on My Widgets, this path is contained within both links, and so the Widget panel is
    // added twice.
    subnavigationPaths = [...new Set(subnavigationPaths)]

    if (subnavigationPaths?.length) {
      sidebarBreadcrumb.push(sidebarPanelName, ...subnavigationPaths)
      return
    }
  })

  return sidebarBreadcrumb
}

/**
 * If there isn't a sidebar link with the exact url of the current path,
 * this helper will try to partially match the current path to a parent path.
 * I.e. it will return true for a sidebar link that is included within the current path
 */
const currentPathBelongsToSidebarPath = ({
  currentPath,
  sidebarLinkUrl,
  relevantCurrentPath
}: Pick<FindSidebarPanelsProps, 'relevantCurrentPath'> & {
  currentPath: string
  sidebarLinkUrl: string
}) => {
  let belongs = false

  // MyBusiness sidebar links include the full url rather than just the path
  // e.g. https://mybusiness.eola.co/outlets/active-company-wales/widgets/settings
  // instead of just /outlets/active-company-wales/widgets/settings
  // We remove the root here so that paths is populated correctly.
  let sidebarLinkUrlNoRoot = sidebarLinkUrl
  if (sidebarLinkUrl.includes('mybusiness')) {
    const root = sidebarLinkUrl.split('/')[2]
    sidebarLinkUrlNoRoot = sidebarLinkUrl.split(root)[1]
  }

  // Most sidebar paths begin with `outlets/[outletSlug]`.
  // Therefore when determining whether the current path belongs to a sidebar path
  // we remove this section to make sure we only return true for sidebar paths
  // that match deeper than just on the outlet section.
  const undesiredPaths = determinePossiblePaths(
    currentPath.split(relevantCurrentPath)[0]
  )

  const paths = determinePossiblePaths(sidebarLinkUrlNoRoot)
    .filter(path => !undesiredPaths.includes(path))
    .reverse()

  belongs = paths.some(path => currentPath.startsWith(path))

  return belongs
}

/**
 * It generates all of the component paths that make up the path passed.
 * e.g. Given a path of  'outlets/[outletSlug]/instructors'
 * return ['outlets/', 'outlets/[outletSlug]', 'outlets/[outletSlug]/instructors']
 */
const determinePossiblePaths = (path: string) => {
  const paths = path.split('/').filter(x => x)

  return paths.map((_, index) => '/' + paths.slice(0, index + 1).join('/'))
}
